
import {
  AnalyticsEvent,
  AnalyticsEventPayloadMap,
  AnalyticsEventType,
} from '../../types/analytics';
import { appConfig } from '../../config/env';
import { IAnalyticsService } from './IAnalyticsService';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

type SafeParams = Record<
  string,
  string | number | boolean | Array<Record<string, string | number>>
>;

export class GoogleAnalyticsService implements IAnalyticsService {
  private readonly events: AnalyticsEvent[] = [];
  private readonly anonymousId =
    `anon_${Math.random().toString(36).slice(2, 10)}`;

  private readonly measurementId =
    appConfig.analytics.gaMeasurementId?.trim() ?? '';

  private initialized = false;

  constructor() {
    this.initialize();
  }

  private initialize(): void {
    if (
      typeof window === 'undefined' ||
      !this.measurementId ||
      this.initialized
    ) {
      return;
    }

    this.initialized = true;
    window.dataLayer = window.dataLayer || [];

    window.gtag =
      window.gtag ||
      ((...args: unknown[]) => {
        window.dataLayer!.push(args);
      });

    window.gtag('js', new Date());
    window.gtag('config', this.measurementId, {
      send_page_view: false,
    });

    const script = document.createElement('script');
    script.async = true;
    script.src =
      `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
        this.measurementId,
      )}`;

    script.onerror = () => {
      console.warn('Google Analytics script failed to load.');
    };

    document.head.appendChild(script);
  }

  private send(eventName: string, params: SafeParams = {}): void {
    if (
      typeof window !== 'undefined' &&
      this.measurementId &&
      window.gtag
    ) {
      window.gtag('event', eventName, params);
    }
  }

  track<T extends AnalyticsEventType>(
    event: T,
    properties: AnalyticsEventPayloadMap[T],
  ): void {
    const record: AnalyticsEvent<T> = {
      event,
      timestamp: Date.now(),
      url:
        typeof window !== 'undefined'
          ? window.location.pathname
          : '/',
      properties: properties as AnalyticsEvent<T>['properties'],
      anonymousId: this.anonymousId,
    };

    this.events.push(record as AnalyticsEvent);

    // Explicit allowlist: never forward tokens, payment IDs,
    // order IDs, email hashes, or arbitrary payload properties.
    switch (event) {
      case 'page_view': {
        const p = properties as AnalyticsEventPayloadMap['page_view'];
        this.trackPageView(p.path, p.title);
        return;
      }

      case 'lead_magnet_view': {
        const p = properties as AnalyticsEventPayloadMap['lead_magnet_view'];
        this.send('lead_magnet_view', {
          slug: p.slug,
          title: p.title,
        });
        return;
      }

      case 'lead_magnet_submission': {
        const p = properties as AnalyticsEventPayloadMap['lead_magnet_submission'];
        this.send('generate_lead', {
          lead_type: 'lead_magnet',
          slug: p.slug,
        });
        return;
      }

      case 'lead_magnet_download': {
        const p = properties as AnalyticsEventPayloadMap['lead_magnet_download'];
        this.send('lead_magnet_download', { slug: p.slug });
        return;
      }

      case 'product_view': {
        const p = properties as AnalyticsEventPayloadMap['product_view'];
        this.send('view_item', {
          currency: p.currency,
          value: p.priceINR,
          items: [{ item_id: p.slug, price: p.priceINR }],
        });
        return;
      }

      case 'buy_click': {
        const p = properties as AnalyticsEventPayloadMap['buy_click'];
        this.send('select_item', {
          item_list_name: 'Digital products',
          items: [{ item_id: p.slug, price: p.priceINR }],
        });
        return;
      }

      case 'checkout_start': {
        const p = properties as AnalyticsEventPayloadMap['checkout_start'];
        this.send('begin_checkout', {
          currency: 'INR',
          value: p.amount,
          items: [{ item_id: p.slug }],
        });
        return;
      }

      case 'payment_success': {
        const p = properties as AnalyticsEventPayloadMap['payment_success'];
        this.send('purchase', {
          currency: 'INR',
          value: p.amount,
          // Deliberately omit orderId and paymentId.
        });
        return;
      }

      case 'payment_failure': {
        const p = properties as AnalyticsEventPayloadMap['payment_failure'];
        this.send('payment_failure', {
          ...(p.errorCode ? { error_code: p.errorCode } : {}),
          // Omit errorMessage because it may contain sensitive details.
        });
        return;
      }

      case 'product_download': {
        const p = properties as AnalyticsEventPayloadMap['product_download'];
        this.send('product_download', {
          product_id: p.productId,
          // Deliberately omit fileId.
        });
        return;
      }

      case 'consultation_click': {
        const p = properties as AnalyticsEventPayloadMap['consultation_click'];
        this.send('consultation_click', {
          service_category: p.serviceCategory,
          source: p.source,
        });
        return;
      }

      case 'whatsapp_click': {
        const p = properties as AnalyticsEventPayloadMap['whatsapp_click'];
        this.send('whatsapp_click', {
          intent: p.intent,
          page: p.page,
        });
        return;
      }

      case 'contact_submission': {
        const p = properties as AnalyticsEventPayloadMap['contact_submission'];
        this.send('generate_lead', { topic: p.topic });
        return;
      }

      default:
        return;
    }
  }

  trackPageView(path: string, title?: string): void {
    this.send('page_view', {
      page_path: path,
      page_title: title || document.title,
      page_location:
        typeof window !== 'undefined' ? window.location.href : path,
    });
  }

  identify(_userId: string, _traits?: Record<string, unknown>): void {
    // Do not send user IDs, traits, or personal data to GA4.
  }

  getEventsBuffer(): readonly AnalyticsEvent[] {
    return [...this.events];
  }
}
